import React, { useCallback, useRef } from 'react';
import { useMutation } from '@apollo/client';
import { useFormContext, useWatch } from 'react-hook-form';
import { Stack, Typography } from '@mui/material';
import { CurrencyInputField, InputField, NumberFormatField, SelectField } from 'components/form/rhf';
import { formatCurrency } from 'utils';
import { QuoteDepositType } from '@trnsact/trnsact-shared-types';
import { CUSTOMER_DEPOSIT_TYPE_OPTIONS } from '../../../constants';
import { useDealBuilderContext } from '../../../context/DealBuilderContext';
import { dealBuilderApi, UpdateDealBuilderQuoteResponse, UpdateDealBuilderQuoteVariables } from '../../../api';
import { DealBuilderFormValues } from '../../../types';
import { roundToCents } from '../../../lib';
import { quoteAPI } from '../../../../quote/api';

export const CustomerDepositForm = () => {
  const { control, getValues } = useFormContext<DealBuilderFormValues>();
  const {
    activeEntity,
    pricing: { priceCalc },
    sourceData: { vo },
  } = useDealBuilderContext();
  const vendorOpportunityId = vo?.vendorOpportunityId ?? '';

  const [updateDealBuilderQuoteMutation] = useMutation<UpdateDealBuilderQuoteResponse, UpdateDealBuilderQuoteVariables>(
    dealBuilderApi.updateDealBuilderQuote,
    {
      refetchQueries: [quoteAPI.getQuoteCalcById],
      awaitRefetchQueries: true,
    }
  );
  const quoteTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const updateQuote = useCallback(
    (input: Record<string, unknown>) => {
      const quoteId = activeEntity?.quote?.quoteId;
      if (!quoteId) return;
      clearTimeout(quoteTimerRef.current);
      quoteTimerRef.current = setTimeout(async () => {
        await updateDealBuilderQuoteMutation({ variables: { quoteId, input } });
      }, 1500);
    },

    [activeEntity, updateDealBuilderQuoteMutation]
  );

  const depositType = useWatch({ control, name: 'pricing.customerDeposit.type' });
  const depositAmount = useWatch({ control, name: 'pricing.customerDeposit.value' });

  const { invoice, equipment, attachments } = priceCalc?.totals ?? {};

  const totalBeforeDeposit =
    (equipment?.price ?? 0) +
    (attachments?.price ?? 0) +
    (invoice?.fees ?? 0) +
    (invoice?.taxes ?? 0) +
    (equipment?.fees ?? 0) +
    (equipment?.taxes ?? 0) +
    (attachments?.fees ?? 0) +
    (attachments?.taxes ?? 0);
  const calculatedDepositAmount =
    depositType === QuoteDepositType.Percentage ? (depositAmount / 100) * totalBeforeDeposit : depositAmount;

  const persistDeposit = (overrideType?: QuoteDepositType) => {
    const { type, value, notes } = getValues('pricing.customerDeposit');
    const resolvedType = overrideType ?? type;
    updateQuote({
      vendorOpportunityId,
      deposit: { type: resolvedType, value, notes },
    });
  };

  const handleBlur = () => persistDeposit();

  return (
    <Stack gap="1rem">
      <Typography variant="h6" component="span">
        Customer Deposit
      </Typography>
      <Stack direction="row" gap="1rem">
        <SelectField<DealBuilderFormValues>
          name="pricing.customerDeposit.type"
          label="Deposit Type"
          variant="outlined"
          control={control}
          options={CUSTOMER_DEPOSIT_TYPE_OPTIONS}
          extraAction={nextValue => persistDeposit(nextValue as QuoteDepositType)}
        />
        {depositType === QuoteDepositType.Flat ? (
          <CurrencyInputField<DealBuilderFormValues>
            name="pricing.customerDeposit.value"
            label="Deposit Amount"
            control={control}
            textFieldProps={{ variant: 'outlined', onBlur: handleBlur }}
          />
        ) : (
          <NumberFormatField<DealBuilderFormValues>
            name="pricing.customerDeposit.value"
            label="Deposit (%)"
            control={control}
            decimalScale={2}
            allowNegative={false}
            textFieldProps={{ variant: 'outlined', size: 'small', onBlur: handleBlur }}
          />
        )}
        <InputField<DealBuilderFormValues>
          name="pricing.customerDeposit.notes"
          label="Notes"
          control={control}
          inputProps={{
            placeholder: 'Enter note if deposit was paid with data received or is yet to be paid',
            variant: 'outlined',
            onBlur: handleBlur,
          }}
        />
      </Stack>
      <Stack direction="row">
        <Typography variant="body1">
          Deposit Amount:{' '}
          <Typography component="span" variant="body2" fontWeight={600}>
            {formatCurrency(calculatedDepositAmount, true)}
          </Typography>
        </Typography>
      </Stack>
    </Stack>
  );
};
